import { mapActions, mapGetters } from 'vuex'
import {
  GET_CART_ITEMS,
  GET_IS_FIRST_TIME_RECIPIENT,
  GET_IS_EXPIRED,
  GET_IS_LOCKED,
  GET_IS_PRINTABLE_RL,
  GET_REDEMPTION_HISTORY,
  GET_REWARD_LINK,
  GET_REWARD_LINK_PARENT_REFERENCE_LINE_ITEM_ID,
  GET_REWARD_LINK_PLATFORM_IDENTITY_HASH,
  GET_REWARD_LINK_UTID,
  TYPE_OF_REWARD_LINK
} from '@/store/get-types'
import {
  RX_ADA_PROACTIVE,
  SVP_ADA_CHATBOT_ENABLED
} from '@/plugins/split/ActiveSplits'
import { FETCH_REDEMPTION_HISTORY } from '@/store/action-types'

export const INACTIVE_WITH_ITEMS_IN_CART_THRESHOLD = 30000 // 30s of no activity (based on clicks)
export const FIRST_TIME_RECIPIENT_PROACTIVE_CHATBOT_THRESHOLD = 15000 // 15s after mount
export const REDEMPTION_HELP_CAMPAIGN_KEY = 'RL_Redemption_Help'
export const REDEMPTION_HISTORY_PAGE_WITH_0_REDEMPTIONS_CAMPAIGN_KEY =
  'Redemption_History_Page_with_0_Redemptions'
export const REWARD_LINK_CATALOG_PAGE_CAMPAIGN_KEY = 'Reward_Link_Catalog_Page'
export const MAX_HISTORY_ITEMS_IN_META_FIELDS = 5

export default {
  name: 'AdaContainer',
  data: () => ({
    inactivityTracker: null,
    firstTimeTimeout: null,
    campaignMetaFields: {}
  }),
  computed: {
    ...mapGetters({
      cartItems: GET_CART_ITEMS,
      isExpired: GET_IS_EXPIRED,
      isFirstTimeRecipient: GET_IS_FIRST_TIME_RECIPIENT,
      isLocked: GET_IS_LOCKED,
      isPrintedRewardLink: GET_IS_PRINTABLE_RL,
      itemType: TYPE_OF_REWARD_LINK,
      parentOrderReferenceId: GET_REWARD_LINK_PARENT_REFERENCE_LINE_ITEM_ID,
      platformIdentityHash: GET_REWARD_LINK_PLATFORM_IDENTITY_HASH,
      redemptionHistory: GET_REDEMPTION_HISTORY,
      rewardLink: GET_REWARD_LINK,
      utid: GET_REWARD_LINK_UTID
    }),
    adaChatbotEnabled () {
      return this.$isSplitEnabled(SVP_ADA_CHATBOT_ENABLED)
    },
    proactiveSplitEnabled () {
      return this.$isSplitEnabled(RX_ADA_PROACTIVE)
    },
    previousRedemptionLineItems () {
      return this.redemptionHistory || []
    },
    previousRedemptionLineItemIds () {
      return this.previousRedemptionLineItems
        .map(({ referenceLineItemId }) => referenceLineItemId)
        .join(', ')
    },
    metaFields () {
      const campaignMetaFields = this.campaignMetaFields || {}
      const { expirationDate } = this.rewardLink || {}
      const baseMetaFields = {
        cart_total_items: this.cartItems.length,
        expiration_date: expirationDate,
        is_expired: this.isExpired,
        is_first_time_recipient: this.isFirstTimeRecipient,
        is_locked: this.isLocked,
        is_printed_reward_link: this.isPrintedRewardLink,
        reference_id: this.parentOrderReferenceId,
        platform_identity_hash: this.platformIdentityHash,
        redeemed_items: this.previousRedemptionLineItemIds,
        redemption_type: this.itemType,
        total_redeemed_items: this.previousRedemptionLineItems.length,
        utid: this.utid,
        ...campaignMetaFields
      }
      return this.previousRedemptionLineItems
        .slice(0, MAX_HISTORY_ITEMS_IN_META_FIELDS)
        .reduce(this.redeemedItemMetaFieldsReducer, baseMetaFields)
    }
  },
  watch: {
    async adaChatbotEnabled () {
      // we have fetched split info, and chatbot split is enabled.
      if (this.adaChatbotEnabled) {
        this.trackInactivityIfFirstTimeAndCartNotEmpty()
        // Using arbitrarily large count to force fetching of entire history
        const count = 999
        await this.fetchRedemptionHistory(count)
        await this.setMetaFields()
      }
    },
    cartItems () {
      this.trackInactivityIfFirstTimeAndCartNotEmpty()
    },
    async metaFields () {
      await this.setMetaFields()
    },
    isFirstTimeRecipient () {
      this.startCampaignTimerForFirstTimeRecipients()
    }
  },
  methods: {
    ...mapActions({
      fetchRedemptionHistory: FETCH_REDEMPTION_HISTORY
    }),
    clearInactivityTracker () {
      if (this.inactivityTracker) {
        clearTimeout(this.inactivityTracker)
        this.inactivityTracker = null
      }
    },
    triggerInactiveWithItemsInCartCampaign () {
      this.stopTrackingInactivity()
      this.$adaChatbot.triggerCampaign(REWARD_LINK_CATALOG_PAGE_CAMPAIGN_KEY)
    },
    async setMetaFields () {
      await this.$adaChatbot.setMetaFields(this.metaFields)
    },
    redeemedItemMetaFieldsReducer (accumulator, item, index) {
      const prefix = `redeemed_item${index + 1}`
      const {
        brandName,
        createdAt,
        currencyCode,
        lineItemTotal,
        referenceLineItemId
      } = item || {}
      return {
        ...accumulator,
        [`${prefix}_brand`]: brandName,
        [`${prefix}_amount`]: this.$money(lineItemTotal, currencyCode),
        [`${prefix}_date`]: createdAt,
        [`${prefix}_referenceId`]: referenceLineItemId
      }
    },
    resetInactivityTracker () {
      this.clearInactivityTracker()
      this.inactivityTracker = setTimeout(
        this.triggerInactiveWithItemsInCartCampaign,
        INACTIVE_WITH_ITEMS_IN_CART_THRESHOLD
      )
    },
    stopTrackingInactivity () {
      this.clearInactivityTracker()
      document.removeEventListener('click', this.resetInactivityTracker)
      document.removeEventListener('touchstart', this.resetInactivityTracker)
    },
    trackInactivityIfFirstTimeAndCartNotEmpty () {
      if (
        this.cartItems.length &&
        this.adaChatbotEnabled &&
        this.proactiveSplitEnabled
      ) {
        this.resetInactivityTracker()
        document.addEventListener('click', this.resetInactivityTracker)
        document.addEventListener('touchstart', this.resetInactivityTracker)
      }
    },
    startCampaignTimerForFirstTimeRecipients () {
      if (
        this.isFirstTimeRecipient &&
        this.adaChatbotEnabled &&
        this.proactiveSplitEnabled
      ) {
        this.firstTimeTimeout = setTimeout(
          this.triggerFirstTimeRecipientCampaign,
          FIRST_TIME_RECIPIENT_PROACTIVE_CHATBOT_THRESHOLD
        )
      }
    },
    async triggerFirstTimeRecipientCampaign () {
      await this.$adaChatbot.triggerCampaign(
        REWARD_LINK_CATALOG_PAGE_CAMPAIGN_KEY
      )
    }
  },
  async mounted () {
    if (this.adaChatbotEnabled) {
      await this.$adaChatbot.startChatbot()
    }
    this.trackInactivityIfFirstTimeAndCartNotEmpty()
    this.startCampaignTimerForFirstTimeRecipients()
  },
  render () {
    return this.$scopedSlots.default({})
  },
  beforeDestroy () {
    this.stopTrackingInactivity()
  }
}
